import { campaignQuestIdAuthenticateAPI, campaignQuestIdIssueAPI } from 'api/campaign'
import { Status } from 'api/common/statuses'
import InputField from 'components/InputField'
import * as React from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'

type Pattern = 'authenticate' | 'issue' | null

interface Props {
  show: boolean
  onHide: () => void
  onComplete: (atemQuestId: string) => void
  formEmail: string
  formName: string
  formPassword: string
  hash: string
}

/**
 * Quest 認証 / 発行 API の status 別エラー文言マップ (要件書 §3.2.1 / Rev.17 S40)
 *
 * QuestAuthFailed は ELL_Portal API §3.3 INVALID_CREDENTIALS の統合 status で、
 * メアド不存在とパスワード不一致を区別しない情報漏洩抑制ポリシーに対応する。
 * QuestAccountDisabled は account_status != 1 (運営側で無効化された Quest アカウント)。
 * 上記以外 (InternalError / RATE_LIMITED / NETWORK_ERROR 等) は一般エラー文言にフォールバック。
 */
const QUEST_AUTH_FAILED_MESSAGE = 'クエストIDのメアドまたはパスワードが正しくありません。'
const QUEST_ACCOUNT_DISABLED_MESSAGE = 'クエストIDのアカウントが無効化されています。Quest 運営にお問い合わせください。'
const QUEST_AUTHENTICATE_FALLBACK_MESSAGE = 'クエストIDの認証に失敗しました。時間をおいて再度お試しください。'
const QUEST_ISSUE_FALLBACK_MESSAGE = 'クエストIDの発行に失敗しました。時間をおいて再度お試しください。'

const resolveAuthenticateErrorMessage = (status: Status): string => {
  switch (status) {
    case Status.QuestAuthFailed:
      return QUEST_AUTH_FAILED_MESSAGE
    case Status.QuestAccountDisabled:
      return QUEST_ACCOUNT_DISABLED_MESSAGE
    default:
      return QUEST_AUTHENTICATE_FALLBACK_MESSAGE
  }
}

const resolveIssueErrorMessage = (status: Status): string => {
  switch (status) {
    case Status.QuestAccountDisabled:
      return QUEST_ACCOUNT_DISABLED_MESSAGE
    default:
      return QUEST_ISSUE_FALLBACK_MESSAGE
  }
}

/**
 * Quest ID 取得モーダル (要件書 §3.2.1 / Rev.16 D5 / Rev.17 S40)
 *
 * パターン1 (ID所持): モーダル内で Quest メアド + パスワード入力 → authenticate API.
 * パターン2 (ID未所持): メインフォームの email/name/password を転送 → issue API.
 *
 * Quest パスワードはモーダル閉時 / API 呼出後に必ず破棄 (要件書 §3.2.1 line 376).
 * BE 経由で ELL_Portal Quest 認証 / ID 自動発行 API に到達 (BE PR #334 / #335 で実装).
 * 失敗時は status (QuestAuthFailed / QuestAccountDisabled / InternalError) に応じた
 * 専用エラーメッセージを表示する (resolveAuthenticateErrorMessage / resolveIssueErrorMessage).
 */
const CampaignQuestIdModal: React.FC<Props> = ({ show, onHide, onComplete, formEmail, formName, formPassword, hash }) => {
  const [pattern, setPattern] = React.useState<Pattern>(null)
  const [questEmail, setQuestEmail] = React.useState<string>(formEmail)
  const [questPassword, setQuestPassword] = React.useState<string>('')
  const [loading, setLoading] = React.useState<boolean>(false)
  const [errorMessage, setErrorMessage] = React.useState<string>('')

  React.useEffect(() => {
    if (show) {
      setPattern(null)
      setQuestEmail(formEmail)
      setQuestPassword('')
      setErrorMessage('')
      setLoading(false)
    }
  }, [show, formEmail])

  const handleClose = (): void => {
    setQuestPassword('')
    onHide()
  }

  const handleAuthenticate = async (): Promise<void> => {
    setLoading(true)
    setErrorMessage('')
    await campaignQuestIdAuthenticateAPI({ email: questEmail, password: questPassword, hash }, (status, response) => {
      setLoading(false)
      setQuestPassword('')
      if (status === Status.Success && response?.atemQuestId) {
        onComplete(response.atemQuestId)
        return
      }
      setErrorMessage(resolveAuthenticateErrorMessage(status))
    })
  }

  const handleIssue = async (): Promise<void> => {
    setLoading(true)
    setErrorMessage('')
    await campaignQuestIdIssueAPI({ email: formEmail, name: formName, password: formPassword, hash }, (status, response) => {
      setLoading(false)
      if (status === Status.Success && response?.atemQuestId) {
        onComplete(response.atemQuestId)
        return
      }
      setErrorMessage(resolveIssueErrorMessage(status))
    })
  }

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>クエストID取得</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {pattern === null && (
          <div className="d-grid gap-3">
            <p className="mb-2">クエストIDをすでにお持ちですか?</p>
            <Button variant="primary" onClick={() => setPattern('authenticate')}>
              クエストIDを持っている
            </Button>
            <Button variant="outline-primary" onClick={() => setPattern('issue')}>
              クエストIDを持っていない
            </Button>
          </div>
        )}

        {pattern === 'authenticate' && (
          <div>
            <p className="mb-3">クエストIDのメアドとパスワードを入力してください。</p>
            <InputField name="quest-email" type="email" labelTop="クエストメールアドレス" value={questEmail} setValue={setQuestEmail} bold />
            <InputField name="quest-password" type="password" labelTop="クエストパスワード" value={questPassword} setValue={setQuestPassword} className="mt-3" bold />
            <Button variant="primary" className="w-100 mt-4" onClick={handleAuthenticate} disabled={loading || !questEmail || !questPassword}>
              {loading ? <Spinner size="sm" /> : '認証する'}
            </Button>
          </div>
        )}

        {pattern === 'issue' && (
          <div>
            <p className="mb-3">申込メールアドレスと氏名・ログインパスワードでクエストIDを自動発行します。</p>
            <InputField name="quest-email-readonly" type="email" labelTop="クエストメールアドレス" value={formEmail} disabled bold />
            <Button variant="primary" className="w-100 mt-4" onClick={handleIssue} disabled={loading}>
              {loading ? <Spinner size="sm" /> : '発行する'}
            </Button>
          </div>
        )}

        {errorMessage && <p className="note text-danger mt-3">{errorMessage}</p>}
      </Modal.Body>
    </Modal>
  )
}

export default CampaignQuestIdModal
